import React, { useEffect, useState } from "react";

import {
  calculateFootprintingProgress,
  formatNumericCurrency,
  queryIsLoading,
  useGetFootprintingCropFootprints,
  useGetFootprintingFarmFootprint,
  useTranslation,
} from "utils";

import { CarbonProgram } from "models";

import { Checkbox, Flex, Form, Icon, OutlinedCard, SkeletonWrapper } from "components";

export const SubmitForVerificationCard = ({
  farmId,
  carbonProgram,
  year,
  formSubmitted = false,
  onCheckboxChange,
}: {
  farmId?: string;
  carbonProgram?: CarbonProgram;
  year: number;
  formSubmitted: boolean;
  onCheckboxChange: (allChecked: boolean) => void;
}) => {
  const { t } = useTranslation("footprinting");
  const [checkbox, setCheckbox] = useState({ payoutTerms: false, editTerms: false });
  const [loading, setLoading] = useState<boolean>();

  const farmProgressQuery = useGetFootprintingFarmFootprint(farmId, year);
  const cropProgressQueries = useGetFootprintingCropFootprints(farmId, [year], carbonProgram?.farmLevelFootprinting?.programCrops);

  const { totalPayout } = calculateFootprintingProgress(
    farmProgressQuery.data,
    cropProgressQueries.filter((cpq) => !!cpq.data).map((cpq) => cpq.data)
  );

  const allChecked = Object.values(checkbox).every((value) => value === true);

  useEffect(() => {
    onCheckboxChange(allChecked);
  }, [checkbox, onCheckboxChange]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading([farmProgressQuery, ...cropProgressQueries].some(queryIsLoading) || carbonProgram === undefined);
  }, [farmProgressQuery, cropProgressQueries, carbonProgram]);

  const handleCheckboxChange = (checkboxName: keyof typeof checkbox) => {
    setCheckbox((prev) => ({
      ...prev,
      [checkboxName]: !prev[checkboxName],
    }));
  };

  return (
    <Flex vertical>
      <Flex align="center" className="padding-150 padding-left-100">
        <SkeletonWrapper className="margin-0 skeleton-rectangular" width="50%" height="2rem" type="block" isLoading={loading}>
          <Icon size="l" iconName="klim/page-checkmark" />
          <h3 className="margin-left-100">{t("submitForVerificationCard.title")}</h3>
        </SkeletonWrapper>
      </Flex>

      <OutlinedCard dataTestId="submit-verification-card">
        <Flex vertical gap={24}>
          <SkeletonWrapper className="margin-0 skeleton-rectangular" width="40%" height="1.5rem" type="block" isLoading={loading}>
            <h4>{t("submitForVerificationCard.subTitle")}</h4>
          </SkeletonWrapper>
          <SkeletonWrapper className="margin-0 skeleton-rectangular" type="paragraph" isLoading={loading}>
            <span className="paragraph-m grey-600-text">{t("submitForVerificationCard.description")}</span>
          </SkeletonWrapper>
        </Flex>

        <Flex vertical className="margin-top-100">
          <hr className="margin-0" />
          <Flex align="center" justify="space-between" className="margin-top-100 margin-bottom-200">
            <SkeletonWrapper className="skeleton-rectangular" height="1rem" width="50%" type="block" isLoading={loading}>
              <span className="paragraph-m-bold">{t("submitForVerificationCard.total", { year })}</span>
            </SkeletonWrapper>
            <SkeletonWrapper className="skeleton-rectangular" height="1rem" type="block" isLoading={loading}>
              <div className="footprinting-payout-card-amount">
                <Icon iconName="tabler/clock" />
                {formatNumericCurrency(totalPayout, { currency: carbonProgram?.currency })}
              </div>
            </SkeletonWrapper>
          </Flex>

          <Flex vertical gap={16}>
            <Form.Item
              validateStatus={!checkbox.payoutTerms && formSubmitted ? "error" : ""}
              help={!checkbox.payoutTerms && formSubmitted ? t("submitForVerificationCard.tAndCErrorMessage") : ""}
              className={!checkbox.payoutTerms && formSubmitted ? "checkbox-error" : ""}
            >
              <SkeletonWrapper className="margin-0 skeleton-rectangular" height="1rem" type="block" isLoading={loading}>
                <Checkbox checked={checkbox.payoutTerms} onChange={() => handleCheckboxChange("payoutTerms")} />
                <span className="paragraph-m">{t("submitForVerificationCard.tAndCPayout", { year })}*</span>
              </SkeletonWrapper>
            </Form.Item>

            <Form.Item
              validateStatus={!checkbox.editTerms && formSubmitted ? "error" : ""}
              help={!checkbox.editTerms && formSubmitted ? t("submitForVerificationCard.tAndCErrorMessage") : ""}
              className={!checkbox.editTerms && formSubmitted ? "checkbox-error" : ""}
            >
              <SkeletonWrapper className="margin-0 skeleton-rectangular" height="1rem" type="block" isLoading={loading}>
                <Checkbox checked={checkbox.editTerms} onChange={() => handleCheckboxChange("editTerms")} />
                <span className="paragraph-m">{t("submitForVerificationCard.tAndCEdit", { year })}*</span>
              </SkeletonWrapper>
            </Form.Item>
          </Flex>
        </Flex>
      </OutlinedCard>
    </Flex>
  );
};
